import { useCallback, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Copy,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  Paperclip,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { authorizedFetch } from '@/lib/api'
import { cn } from '@/lib/utils'
import { formatBytes } from '@/utils/format-bytes'
import { getAttachmentIconForMime } from '@/components/chat/attachment-chip'
import { ThreadStatusPill } from './thread-meta'

export interface ThreadFile {
  id: string
  original_name: string
  mime_type: string
  size_bytes: number
  status: string
  file_url: string
  created_at?: string
  scope_hints?: string[]
  origin?: string
}

interface PendingUpload {
  id: string
  name: string
  size: number
  mime: string
  status: 'uploading' | 'error'
  error?: string
}

interface ThreadFilesPanelProps {
  threadId: string
  className?: string
  /** When true, omit outer Card + section title (parent provides EntityManageSection chrome). */
  embedded?: boolean
}

export function ThreadFilesPanel({ threadId, className, embedded = false }: ThreadFilesPanelProps) {
  const queryClient = useQueryClient()
  const [isDragging, setIsDragging] = useState(false)
  const [pending, setPending] = useState<PendingUpload[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)

  const { data: files = [], isLoading, isError, refetch } = useQuery<ThreadFile[]>({
    queryKey: ['thread-files', threadId],
    queryFn: async () => {
      const r = await authorizedFetch(`/api/files/threads/${threadId}/files`)
      if (!r.ok) throw new Error('Failed to load files')
      const data = await r.json()
      return Array.isArray(data) ? (data as ThreadFile[]) : []
    },
  })

  const uploadOne = useCallback(
    async (file: File) => {
      const tempId = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`
      setPending((p) => [
        ...p,
        { id: tempId, name: file.name, size: file.size, mime: file.type, status: 'uploading' },
      ])
      try {
        const fd = new FormData()
        fd.append('file', file)
        const r = await authorizedFetch(`/api/files/threads/${threadId}/upload`, {
          method: 'POST',
          body: fd,
        })
        if (!r.ok) {
          const text = await r.text().catch(() => '')
          throw new Error(text || 'Upload failed')
        }
        setPending((p) => p.filter((u) => u.id !== tempId))
        await queryClient.invalidateQueries({ queryKey: ['thread-files', threadId] })
        await queryClient.invalidateQueries({ queryKey: ['thread', threadId] })
        await queryClient.invalidateQueries({ queryKey: ['threads'] })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setPending((p) =>
          p.map((u) => (u.id === tempId ? { ...u, status: 'error', error: message } : u)),
        )
      }
    },
    [threadId, queryClient],
  )

  const handleFiles = useCallback(
    async (fl: FileList | File[] | null) => {
      if (!fl) return
      const arr = Array.from(fl)
      if (arr.length === 0) return
      for (const f of arr) {
        await uploadOne(f)
      }
    },
    [uploadOne],
  )

  const onPickClick = () => inputRef.current?.click()

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    dragDepthRef.current += 1
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
  }
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setIsDragging(false)
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dragDepthRef.current = 0
    setIsDragging(false)
    void handleFiles(e.dataTransfer.files)
  }

  const dismissError = (id: string) => setPending((p) => p.filter((u) => u.id !== id))

  const totalCount = files.length

  const body = (
    <div className="space-y-4">
      {embedded ? (
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={onPickClick}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <h2 className="inline-flex items-center gap-2 text-sm font-medium">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            Files
            {totalCount > 0 ? (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs tabular-nums">
                {totalCount}
              </Badge>
            ) : null}
          </h2>
          <Button type="button" size="sm" onClick={onPickClick}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      )}

        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files)
            e.target.value = ''
          }}
        />

        <div
          role="button"
          tabIndex={0}
          onClick={onPickClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onPickClick()
            }
          }}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={cn(
            'group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border/70 hover:border-foreground/30 hover:bg-muted/40',
          )}
        >
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full border border-dashed transition-colors',
              isDragging
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-muted-foreground/40 text-muted-foreground group-hover:text-foreground',
            )}
          >
            <Upload className="h-4 w-4" />
          </div>
          <div className="text-xs">
            <span className="font-medium">{isDragging ? 'Drop to upload' : 'Drag files here'}</span>
            <span className="ml-1 text-muted-foreground">or click to browse</span>
          </div>
        </div>

        {pending.length > 0 ? (
          <ul className="space-y-2">
            {pending.map((u) => {
              const Icon = getAttachmentIconForMime(u.mime)
              return (
                <li
                  key={u.id}
                  className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/20 p-4"
                >
                  <FileIconTile Icon={Icon} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{u.name}</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="tabular-nums">{formatBytes(u.size)}</span>
                      <span aria-hidden="true">·</span>
                      <span className="inline-flex items-center gap-1">
                        {u.status === 'uploading' ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Uploading…
                          </>
                        ) : (
                          <span className="text-destructive">{u.error || 'Upload failed'}</span>
                        )}
                      </span>
                    </div>
                    {u.status === 'uploading' ? <IndeterminateBar className="mt-2" /> : null}
                  </div>
                  {u.status === 'error' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={() => dismissError(u.id)}
                    >
                      Dismiss
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : null}

        {isLoading ? (
          <ul className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-start gap-3 p-4">
                <Skeleton className="h-9 w-9 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </li>
            ))}
          </ul>
        ) : isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <div className="flex items-center justify-between gap-2">
              <span>Could not load files.</span>
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          </div>
        ) : files.length === 0 && pending.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No files in this thread yet. Files you attach in chat also show here.
          </p>
        ) : (
          <ul className="-mx-2 space-y-0.5">
            {files.map((f) => (
              <FileRow key={f.id} file={f} />
            ))}
          </ul>
        )}
    </div>
  )

  if (embedded) {
    return <div className={className}>{body}</div>
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent>{body}</CardContent>
    </Card>
  )
}

function IndeterminateBar({ className }: { className?: string }) {
  return (
    <div className={cn('relative h-1 w-full overflow-hidden rounded-full bg-muted', className)}>
      <div className="absolute inset-y-0 left-0 w-2/3 animate-pulse rounded-full bg-primary/70" />
    </div>
  )
}

function FileIconTile({ Icon }: { Icon: ReturnType<typeof getAttachmentIconForMime> }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted text-muted-foreground">
      <Icon className="h-4 w-4" />
    </div>
  )
}

function FileRow({ file }: { file: ThreadFile }) {
  const Icon = getAttachmentIconForMime(file.mime_type)
  const [copied, setCopied] = useState(false)

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!file.file_url) return
    try {
      await navigator.clipboard.writeText(file.file_url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch {
      // ignore
    }
  }

  const onOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!file.file_url) return
    window.open(file.file_url, '_blank', 'noopener,noreferrer')
  }

  const canOpen = Boolean(file.file_url)

  return (
    <li className="group flex items-start gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/50 focus-within:bg-muted/50">
      <FileIconTile Icon={Icon} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words text-sm font-medium leading-snug">
            {file.original_name}
          </span>
          <ThreadStatusPill status={file.status} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="tabular-nums">{formatBytes(file.size_bytes)}</span>
          {file.mime_type ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="lowercase">{file.mime_type}</span>
            </>
          ) : null}
          {file.origin === 'message' ? (
            <>
              <span aria-hidden="true">·</span>
              <span>From chat</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        {canOpen ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onOpen}
                  aria-label="Open file in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onCopy}
                  aria-label="Copy file link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{copied ? 'Copied' : 'Copy link'}</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="iconXs" aria-label="File actions">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled>No actions available</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </li>
  )
}
